import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useTheme } from '../context/ThemeContext';
import { useBusinessAuth } from '../context/BusinessAuthContext';

export default function MfaEnrollPage() {
  const { t } = useTheme();
  const { listFactors, enrollTotp, challenge, verifyFactor, isAal2 } = useBusinessAuth();
  const [factors, setFactors] = useState(null);
  const [pendingFactor, setPendingFactor] = useState(null); // { factorId, qr, secret }
  const [challengeId, setChallengeId] = useState(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') || '/workspace';

  const cardStyle = { background: t.surface, border: `1px solid ${t.borderSubtle}`, borderRadius: '1rem' };
  const inputStyle = { background: t.inputBgNew, border: `1px solid ${t.inputBorderNew}`, color: t.textPrimary };

  const load = useCallback(async () => {
    try {
      const data = await listFactors();
      const verified = data.totp?.find((f) => f.status === 'verified');
      setFactors(data.totp || []);
      if (verified) {
        const c = await challenge(verified.id);
        setPendingFactor({ factorId: verified.id });
        setChallengeId(c.id);
      }
    } catch (e) {
      toast.error(e?.message || 'Could not load MFA factors');
    }
  }, [listFactors, challenge]);
  useEffect(() => { load(); }, [load]);

  const startEnroll = async () => {
    setBusy(true);
    try {
      const data = await enrollTotp();
      setPendingFactor({ factorId: data.id, qr: data.totp.qr_code, secret: data.totp.secret });
      const c = await challenge(data.id);
      setChallengeId(c.id);
    } catch (e) {
      toast.error(e?.message || 'Could not start MFA enrollment');
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await verifyFactor(pendingFactor.factorId, challengeId, code);
      toast.success('Two-factor authentication verified');
      navigate(next, { replace: true });
    } catch (e2) {
      toast.error(e2?.message || 'Invalid code');
    } finally {
      setBusy(false);
    }
  };

  if (isAal2) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: t.pageBg, color: t.textPrimary }} data-testid="mfa-already-verified">
        <div className="text-center">
          <p className="mb-4">Two-factor authentication is already active for this session.</p>
          <button onClick={() => navigate(next)} className="h-11 px-5 rounded-xl font-bold text-black text-sm" style={{ background: t.accent }}>Continue</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: t.pageBg, color: t.textPrimary, fontFamily: "'Barlow', sans-serif" }} data-testid="mfa-enroll-page">
      <div className="w-full max-w-md p-8" style={cardStyle}>
        <h1 className="text-3xl mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Two-factor authentication</h1>
        <p className="text-sm mb-6" style={{ color: t.textMutedNew }}>
          Required for financial exports, payouts, connection changes and other sensitive actions.
        </p>

        {!pendingFactor && (
          <button
            onClick={startEnroll}
            disabled={busy}
            className="w-full h-11 rounded-xl font-bold text-black text-sm disabled:opacity-50"
            style={{ background: t.accent }}
            data-testid="mfa-start-enroll-button"
          >
            {factors?.length ? 'Verify with your authenticator app' : 'Set up an authenticator app'}
          </button>
        )}

        {pendingFactor && (
          <form onSubmit={verify} className="space-y-4">
            {pendingFactor.qr && (
              <div>
                <p className="text-xs mb-2" style={{ color: t.textMutedNew }}>Scan with Google Authenticator, 1Password or similar:</p>
                <img src={pendingFactor.qr} alt="TOTP QR code" className="rounded-lg" data-testid="mfa-qr-code" />
                <p className="text-[11px] mt-2 break-all" style={{ color: t.textFaint }}>Manual key: {pendingFactor.secret}</p>
              </div>
            )}
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="6-digit code"
              className="w-full h-11 rounded-xl px-4 text-sm text-center tracking-widest"
              style={inputStyle}
              data-testid="mfa-code-input"
              required
            />
            <button
              type="submit"
              disabled={busy}
              className="w-full h-11 rounded-xl font-bold text-black text-sm disabled:opacity-50"
              style={{ background: t.accent }}
              data-testid="mfa-verify-button"
            >
              Verify
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
